// @ts-nocheck
import React, { useEffect, useRef } from 'react';
import {
  WebGLRenderer, Scene, PerspectiveCamera,
  AmbientLight, DirectionalLight,
  IcosahedronGeometry, OctahedronGeometry, TetrahedronGeometry,
  MeshPhongMaterial, Mesh, Color, Clock,
} from 'three';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ExperimentNav from '../../components/ExperimentNav';
import { experiments } from '../../App';
import './LowPolyOrbit.css';

gsap.registerPlugin(ScrollTrigger);

const OBJECTS = [
  { geo: 'icosa', size: 1.3,  color: 0xd4b896, pos: [0,    0,    0   ] as [number, number, number], rotSpeed:  1.0  },
  { geo: 'octa',  size: 0.8,  color: 0x9b7ea6, pos: [2.8,  0.8, -1.2 ] as [number, number, number], rotSpeed: -1.4  },
  { geo: 'tetra', size: 0.7,  color: 0x6b9e8f, pos: [-2.4,-0.6,  0.8 ] as [number, number, number], rotSpeed:  1.2  },
  { geo: 'icosa', size: 0.5,  color: 0xe8c87a, pos: [1.2, -1.8,  1.5 ] as [number, number, number], rotSpeed: -0.8  },
  { geo: 'octa',  size: 0.45, color: 0xc07890, pos: [-1.6, 1.6, -0.8 ] as [number, number, number], rotSpeed:  1.6  },
  { geo: 'tetra', size: 0.35, color: 0x7ab8d4, pos: [3.2, -1.2,  0.4 ] as [number, number, number], rotSpeed: -1.1  },
];

const BG_COLORS = [
  [0.05, 0.05, 0.08],
  [0.08, 0.04, 0.14],
  [0.02, 0.08, 0.12],
];

const LowPolyOrbit: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const exp = experiments.find(e => e.id === 'low-poly-orbit');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // ── Renderer ──────────────────────────────────────
    const renderer = new WebGLRenderer({ antialias: true, canvas });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(canvas.clientWidth, canvas.clientHeight);

    // ── Scene & Camera ────────────────────────────────
    const scene  = new Scene();
    const camera = new PerspectiveCamera(55, canvas.clientWidth / canvas.clientHeight, 0.1, 100);

    // ── Lights ────────────────────────────────────────
    scene.add(new AmbientLight(0xffffff, 0.5));
    const sun = new DirectionalLight(0xfff5e0, 1.5);
    sun.position.set(6, 10, 6);
    scene.add(sun);
    const fill = new DirectionalLight(0x6688cc, 0.8);
    fill.position.set(-6, -4, -6);
    scene.add(fill);

    // ── Meshes ────────────────────────────────────────
    const meshes: Mesh[] = [];
    OBJECTS.forEach(cfg => {
      const geo =
        cfg.geo === 'icosa' ? new IcosahedronGeometry(cfg.size, 0) :
        cfg.geo === 'octa'  ? new OctahedronGeometry(cfg.size, 0)  :
                              new TetrahedronGeometry(cfg.size, 0);

      const mat  = new MeshPhongMaterial({ color: cfg.color, flatShading: true, shininess: 20 });
      const mesh = new Mesh(geo, mat);
      mesh.position.set(...cfg.pos);
      mesh.userData.rotSpeed = cfg.rotSpeed;
      mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
      scene.add(mesh);
      meshes.push(mesh);
    });

    // ── Camera orbit state ────────────────────────────
    const orbit = { theta: 0, phi: 0.25, radius: 9 };

    const updateCamera = () => {
      camera.position.x = orbit.radius * Math.sin(orbit.theta) * Math.cos(orbit.phi);
      camera.position.y = orbit.radius * Math.sin(orbit.phi);
      camera.position.z = orbit.radius * Math.cos(orbit.theta) * Math.cos(orbit.phi);
      camera.lookAt(0, 0, 0);
    };
    updateCamera();

    // ── Background color state ────────────────────────
    const bg = { r: BG_COLORS[0][0], g: BG_COLORS[0][1], b: BG_COLORS[0][2] };
    renderer.setClearColor(new Color(bg.r, bg.g, bg.b));

    // ── Render loop ───────────────────────────────────
    let raf: number;
    const clock = new Clock();

    const animate = () => {
      raf = requestAnimationFrame(animate);
      const dt = clock.getDelta();
      meshes.forEach(m => {
        m.rotation.y += 0.4 * dt * (m.userData.rotSpeed as number);
        m.rotation.x += 0.2 * dt;
      });
      renderer.setClearColor(new Color(bg.r, bg.g, bg.b));
      updateCamera();
      renderer.render(scene, camera);
    };
    animate();

    // ── GSAP ScrollTrigger ────────────────────────────
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: scrollRef.current,
          start: 'top top',
          end:   'bottom bottom',
          scrub: 1.2,
        },
      });

      tl.to(orbit, { theta: Math.PI,     phi:  0.4,   duration: 1 }, 0);
      tl.to(bg,    { r: BG_COLORS[1][0], g: BG_COLORS[1][1], b: BG_COLORS[1][2], duration: 0.6 }, 0.2);

      tl.to(orbit, { theta: Math.PI * 2, phi: -0.15,  duration: 1 }, 1);
      tl.to(bg,    { r: BG_COLORS[2][0], g: BG_COLORS[2][1], b: BG_COLORS[2][2], duration: 0.6 }, 1.3);
    });

    // ── Resize ────────────────────────────────────────
    const onResize = () => {
      camera.aspect = canvas.clientWidth / canvas.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(canvas.clientWidth, canvas.clientHeight);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      ctx.revert();
      renderer.dispose();
    };
  }, []);

  return (
    <div className="lpo-wrapper">
      <ExperimentNav prompt={exp?.prompt} title={exp?.title} />
      <canvas ref={canvasRef} className="lpo-canvas" />
      <div ref={scrollRef} className="lpo-scroll-track">
        <div className="lpo-spacer" />
        <div className="lpo-spacer" />
        <div className="lpo-spacer" />
        <div className="lpo-spacer" />
      </div>
    </div>
  );
};

export default LowPolyOrbit;
